/**
 * 메시지 버블 컴포넌트
 * 채팅 메시지를 카카오톡 스타일 + 레트로 게임 스타일로 표시
 */

import React, { useEffect, useRef, useMemo } from 'react';
import { ChatMessage } from '../../types/chat';
import { BotAvatar } from './BotAvatar';
// @ts-ignore
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * MessageBubble Props
 */
interface MessageBubbleProps {
  /** 메시지 정보 */
  message: ChatMessage;
}

/**
 * LaTeX 인라인 수식을 감지하고 HTML로 변환
 * - \( ... \) 패턴 지원
 * - $...$ 패턴 지원 (참고 메시지용)
 */
const renderContentWithLatex = (text: string): JSX.Element[] => {
  const parts: JSX.Element[] = [];
  // \(...\) 패턴과 $...$ 패턴 모두 지원
  const latexPattern = /\\\((.*?)\\\)|\$([^$]+)\$/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = latexPattern.exec(text)) !== null) {
    // LaTeX 앞의 일반 텍스트
    if (match.index > lastIndex) {
      const plainText = text.substring(lastIndex, match.index);
      parts.push(
        <span key={`text-${key++}`}>{plainText}</span>
      );
    }

    // LaTeX 수식 렌더링 (match[1]은 \(...\), match[2]는 $...$)
    const latexContent = (match[1] || match[2]).trim();
    try {
      const html = katex.renderToString(latexContent, {
        throwOnError: false,
        displayMode: false,
      });
      parts.push(
        <span
          key={`latex-${key++}`}
          dangerouslySetInnerHTML={{ __html: html }}
          className="inline-block mx-1"
        />
      );
    } catch (error) {
      console.error('KaTeX 렌더링 실패:', error);
      parts.push(
        <span key={`latex-error-${key++}`} className="text-red-500">
          {match[0]}
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // 남은 텍스트
  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-${key++}`}>{text.substring(lastIndex)}</span>
    );
  }

  return parts.length > 0 ? parts : [<span key="default">{text}</span>];
};

/**
 * MessageBubble 컴포넌트
 *
 * - 발신자에 따라 왼쪽(봇)/오른쪽(학생) 정렬
 * - 레트로 게임 스타일 버블
 * - 채점봇 메시지는 아바타 표시
 * - 시스템 메시지는 중앙 정렬
 * - 메시지 내 LaTeX 수식 자동 렌더링
 */
export const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const { sender, content, emotion, isSystem, latex } = message;
  const mathRef = useRef<HTMLDivElement>(null);

  // content 내 LaTeX 파싱
  const renderedContent = useMemo(() => renderContentWithLatex(content), [content]);

  /**
   * LaTeX 수식 렌더링
   */
  useEffect(() => {
    if (latex && mathRef.current) {
      try {
        const cleaned = latex.replace(/\\\(|\\\)/g, '').trim();
        katex.render(cleaned, mathRef.current, {
          throwOnError: false,
          displayMode: false,
        });
      } catch (error) {
        console.error('KaTeX 렌더링 실패:', error);
        mathRef.current.textContent = latex;
      }
    }
  }, [latex]);

  /**
   * 시스템 메시지 (중앙 정렬)
   */
  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <div
          className="px-4 py-2 bg-gray-700 text-gray-300
                     rounded-full font-pixel text-xs
                     border-2 border-gray-600
                     shadow-[2px_2px_0px_0px_rgba(0,0,0,0.3)]
                     whitespace-pre-wrap"
        >
          {renderedContent}
        </div>
      </div>
    );
  }

  /**
   * 채점봇 메시지 (왼쪽 정렬, 그린 계열)
   */
  if (sender === 'bot') {
    return (
      <div className="flex items-start gap-2 mb-4 animate-slideInLeft">
        {/* 봇 아바타 */}
        <BotAvatar emotion={emotion} size="medium" />

        {/* 메시지 버블 */}
        <div
          className="max-w-[70%] px-4 py-3
                     bg-[#9bbc0f] text-[#0f380f]
                     rounded-tr-2xl rounded-br-2xl rounded-bl-2xl
                     font-pixel text-sm
                     border-2 border-[#0f380f]
                     shadow-[3px_3px_0px_0px_rgba(0,0,0,0.3)]
                     leading-relaxed whitespace-pre-wrap"
        >
          {renderedContent}

          {/* LaTeX 수식 표시 (E타입, 별도 영역) */}
          {latex && (
            <div className="mt-3 bg-white p-3 rounded-sm flex items-center justify-center">
              <div ref={mathRef} className="text-black text-xl" />
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * 학생 메시지 (오른쪽 정렬, 블루 계열)
   */
  return (
    <div className="flex items-start gap-2 mb-4 justify-end animate-slideInRight">
      {/* 메시지 버블 */}
      <div
        className="max-w-[70%] px-4 py-3
                   bg-[#0f3460] text-white
                   rounded-tl-2xl rounded-bl-2xl rounded-br-2xl
                   font-pixel text-sm
                   border-2 border-[#16213e]
                   shadow-[3px_3px_0px_0px_rgba(0,0,0,0.3)]
                   leading-relaxed whitespace-pre-wrap"
      >
        {renderedContent}
      </div>
    </div>
  );
};
